/**
 * figureImage — 화면에 보이는 그림을 **서버에 보낼 수 있는 이미지**로 바꾼다.
 *
 * 왜 필요한가 (2026-08-30, 오너 지적 「그림을 보고 AI 요청을 누른 건데 원본을 업로드하라니?」):
 * 계약 §4-3 의 재요청 원본 우선순위는 `body.image` → 노드가 pixel 이면 그 src → 이력 보관 원본이다.
 * OCR 에서 이미 도판이 되어 승격된 그림은 뒤 두 경로가 비어 409 `no_original` 이 확정인데
 * (실측: 문제 61592, 56ms 거절 · 그림 이력도 「관측」 한 버전뿐이고 보관 원본 없음),
 * **화면에는 그 그림이 멀쩡히 떠 있다.** 사람에게 다시 올리라고 할 이유가 없다 —
 * 보고 있는 그것을 1순위 `body.image` 로 실어 보내면 된다.
 *
 * 왜 SVG 를 그대로 안 보내나: 서버가 LLM 에 넘기는 것은 **그림**이고, SVG 는 텍스트 문서다.
 * 읽어 주는지가 구현에 달려 있어 확실하지 않다. 캔버스로 한 번 굽고 PNG 로 보내면
 * 어느 쪽이든 「이미지」다 — 픽셀 그림이 원래 가던 길과 같아진다.
 */

/** LLM 이 읽을 만한 크기로 굽는다. 도판은 대개 300~400px 라 그대로 보내면 잘게 나온다. */
const MIN_LONG_SIDE = 900;
const MAX_LONG_SIDE = 1800;

/**
 * 이미지 src(데이터 URI)를 흰 바탕 PNG 데이터 URI 로 굽는다.
 *
 * · 흰 바탕을 먼저 칠하는 이유 — SVG 도판은 배경이 투명이다. 그대로 PNG 로 구우면 투명 화소가
 *   검게 합성되는 뷰어가 있어 선이 사라진다.
 * · 배율 — 긴 변이 MIN_LONG_SIDE 에 닿도록 키우되 MAX_LONG_SIDE 를 넘기지 않는다. 원본보다
 *   줄이지는 않는다(줄이면 읽을 것이 없어진다).
 * · 원격 주소(S3 등)는 넘기지 마라 — 캔버스가 오염되어 toDataURL 이 보안 오류로 죽는다.
 *   이 저장소가 그림에 쓰는 값은 전부 data-URI 다.
 *
 * @returns {Promise<string>} `data:image/png;base64,…`
 */
export default function toPngDataUri(src, { width, height } = {}) {
  return new Promise((resolve, reject) => {
    if (typeof src !== 'string' || !src) {
      reject(new Error('바꿀 그림이 없습니다.'));
      return;
    }
    const img = new Image();
    img.onload = () => {
      try {
        const w0 = img.naturalWidth || width || 0;
        const h0 = img.naturalHeight || height || 0;
        if (!w0 || !h0) { reject(new Error('그림 크기를 읽지 못했습니다.')); return; }
        const long = Math.max(w0, h0);
        // 1 미만으로는 내려가지 않는다 — 키우기만 한다.
        const scale = Math.min(Math.max(MIN_LONG_SIDE / long, 1), MAX_LONG_SIDE / long);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w0 * scale);
        canvas.height = Math.round(h0 * scale);
        const ctx = canvas.getContext('2d');
        // 화면에 칠하는 색이 아니라 **서버로 보낼 PNG 의 바탕**이다. 디자인 토큰(테마·다크모드)의
        // 대상이 아니고, 값이 바뀌면 AI 가 읽는 그림이 바뀐다. 흰 종이가 정본이라 그대로 박는다.
        // eslint-disable-next-line no-restricted-syntax
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        // 캔버스 오염(원격 이미지)·메모리 부족 등. 사유를 삼키지 않는다 — 화면이 올리기로 물러난다.
        reject(new Error('그림을 이미지로 바꾸지 못했습니다.'));
      }
    };
    img.onerror = () => reject(new Error('그림을 읽지 못했습니다.'));
    img.src = src;
  });
}
